import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box, Button, Center, Flex, Text } from "@chakra-ui/react";
import { langStore, translate, useSelecting } from "@noli/business";
import { getRandomSeed } from "@noli/core";

import {
  setAdvanceModeNegativePrompt,
  setAdvancePrompt,
  setGenerateCount,
  setGuidanceScale,
  setMode,
  setModelType,
  setNumSteps,
  setOptimizers,
  setSampler,
  setSeed,
  setTxt2ImgArtist,
  setTxt2ImgStyle,
  setTxt2ImgTexts,
  setUseNegativePrompt,
  setWenxinStyle,
  statusStore,
  updateAnimeSmallModels,
  updatePromptSettings,
  updateSmallModels,
} from "@/stores/status";
import { tasksStore } from "@/stores/tasks";

import { Actions } from "@/utils/api";
import {
  AI_MAX_WH,
  DEFAULT_NEGATIVE_PROMPT_EMBEDDING,
  SD_VERSION,
  Txt2ImgArtists,
  Txt2ImgStyles,
} from "@/utils/constants";
import { Editor_Words } from "@/utils/lang/editor";
import { toast, useOwnToast } from "@/utils/misc";
import {
  ModelTypes,
  MODEL_TYPES_INFO,
  MODEL_TYPES_WITH_THIRD_TYPES,
  MODEL_WORD_TABLE_SOURCES,
  ModeTypes,
  NoliboxModelStatusNameEnum,
  NoliboxModelTypeEnum,
  NOLIBOX_MODEL_TYPES,
  optimizersMap,
  SDSampler,
  ModelTypesWithThirdParty,
  modelDefaultOptimizers,
  defaultOptimizers,
} from "@/modules/editor/constant";

import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { ModelSwitch } from "@/modules/editor/components/common/ModelSwitch";
import TextArea from "@/components/TextArea";
import { Fidelity } from "@/modules/editor/components/common/Fidelity";
import { StyleSelector } from "@/modules/editor/components/common/StyleSelector";
import { ArtistSelector } from "@/modules/editor/components/common/ArtistSelector";
import { CircularSwitch } from "@/modules/editor/components/common/CircularSwitch";

import useImageWithPlaceholder from "@/modules/editor/hooks/useImageWithPlaceholder";
import { OptimizerSelector } from "@/modules/editor/components/common/OptimizerSelector";
import { ModeSwitch } from "@/modules/editor/components/common/ModeSwitch";
import { WordTable } from "@/modules/editor/components/WordTable";
import { NegativePromptSwitch } from "@/modules/editor/components/NegativePromptConfig";
import { NegativePromptWordTable } from "@/modules/editor/components/NegativePromptTable";
import { wordRecordSubmitFilter } from "@/modules/editor/components/WordTable/utils";
import { wordTableStore } from "@/modules/editor/components/WordTable/store";
import { NSlider } from "@/components/NSlider";
import { SamplerSelector } from "@/modules/editor/components/SamplerSelector";
import { InputField } from "@/modules/editor/components/ParamInputField";
import { isThirdParty } from "@/thirdParty/constants";
import MonkeyPanel from "@/thirdParty/monkey/components/MonkeyPanel";
import DraftPanel from "@/thirdParty/draft/components/DraftPanel";
import { Wenxin_Words } from "@/thirdParty/wenxin/lang";
import WenxinStatus from "@/modules/editor/components/WenxinStatus";
import TextTrigger from "@/components/Triggers/TextTrigger";
import { allWenxinStyles } from "@/thirdParty/wenxin/types";
import { WenxinSubmitButton } from "@/thirdParty/wenxin/components/SubmitButton";
import { CapsulesPanel } from "@/components/Capsules";
import { themeStore } from "@/stores/theme";
import { loginStore } from "@/modules/login/store";
import { GenMultiInput } from "@/modules/editor/components/common/GenMultiInput";
import { isAuthUser, isVip } from "@/utils/status";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import { GenerateBizType } from "@/types/creator";
import { SmallModelsSelector } from "@/modules/editor/components/SmallModelsSelector";
import { isNoliboxModel } from "@/modules/editor/components/common/ModelSwitch/util";
import {
  getAdvanceModeMetaData,
  getImg2ImgMetaData,
  getTxt2ImgMetaData,
} from "@/modules/editor/utils/meta";
import events, { MessageType } from "@/utils/events";
import { ReactComponent as robot } from "@/assets/icons/animate_robot.svg";
import Icon from "@/components/Icon";

type RenderConfigType = {
  content: boolean;
  style: boolean;
  artist: boolean;
  fidelity: boolean;
  prompt: boolean;
  promptWordTable: boolean;
  useNegativePrompt: boolean;
  negativePrompt: boolean;
  advanceModeNegativePrompt: boolean;
  negativePromptWordTable: boolean;
  guidance_scale: boolean;
  num_steps: boolean;
  sampler: boolean;
  seed: boolean;
  optimizer: boolean;
  circular: boolean;
  generateCount: boolean;
  smallModels: boolean;
};

const wenxinSubmitSize = "1024*1024";
const updateSmallModelsMethod = {
  nolibox: updateSmallModels,
  dreamlike: updateSmallModels,
  anime: updateAnimeSmallModels,
  "deliberate-general": updateSmallModels,
  "epicrealisim-real": updateSmallModels,
  "revanime-3d-anime": updateAnimeSmallModels,
};
/**
 * 图片转换
 * @returns
 */
const Img2Img: React.FC = () => {
  const lang = langStore.tgt;
  const t = useOwnToast();
  const info = useSelecting("basic")({ fixed: 0 });
  const { type, displayNode, imgWH = { w: 0, h: 0 } } = info ?? {};
  const alias = displayNode?.alias;
  const { panelBg } = themeStore.styles;

  const enableImg2Img = type === "image" || (type === "path" && !alias?.startsWith("landscape"));
  const {
    mode,
    txt2imgTexts: text,
    modelType,
    advancePrompt,
    wenxinStyle,
    promptExpanding,
  } = statusStore;

  const [fidelity, setFidelity] = useState(0.2);
  const [circular, setCircular] = useState(false);
  const isAnime = useMemo(() => modelType === "anime", [modelType]);
  const suitableWH = useMemo(() => {
    if (!displayNode || !imgWH) return { w: 512, h: 512 };

    const wh = { w: imgWH.w, h: imgWH.h };
    if (wh.w * wh.h > AI_MAX_WH ** 2) {
      const { w, h } = displayNode.wh;
      if (w * h < wh.w * wh.h) {
        wh.w = w;
        wh.h = Math.abs(h);
      }
    }

    wh.w = Number(wh.w.toFixed(0));
    wh.h = Number(wh.h.toFixed(0));
    return wh;
  }, [imgWH, displayNode]);

  const sizeString = useMemo(() => {
    if (type === "path") return `${512} × ${512} px`;
    return suitableWH
      ? `${suitableWH.w} × ${suitableWH.h} px`
      : translate(Editor_Words["general-loading"], lang);
  }, [suitableWH, type, lang]);

  const { url, placeholder } = useImageWithPlaceholder({
    type: ["image", "path"],
    condition: (type, displayNode) =>
      type === "path" ? !displayNode?.alias?.startsWith("landscape") : true,
    placeholderConfig: {
      noneSelect: Editor_Words["select-a-picture-or-sketch"],
      notMatchCondition: Editor_Words["current-select-not-a-picture-or-sketch"],
    },
  });

  function handleModelChange(modelType: ModelTypesWithThirdParty) {
    setModelType(modelType);
    if (modelType !== "third-party") {
      setOptimizers(modelDefaultOptimizers[modelType] || defaultOptimizers);
    }
  }

  const config: Record<ModeTypes, Partial<RenderConfigType>> = useMemo(
    () => ({
      simple: {
        content: true,
        fidelity: true,
        style: true,
        artist: true,
        smallModels: isNoliboxModel(modelType) && isAuthUser(),
        optimizer: true,
        circular: isNoliboxModel(modelType) && modelType !== "anime",
        generateCount: isAuthUser(),
      },
      advance: {
        prompt: true,
        promptWordTable: true,
        useNegativePrompt: true,
        advanceModeNegativePrompt: true,
        negativePromptWordTable: true,
        fidelity: true,
        guidance_scale: true,
        num_steps: true,
        sampler: true,
        seed: true,
        circular: isNoliboxModel(modelType) && modelType !== "anime",
        generateCount: isAuthUser(),
        smallModels: isNoliboxModel(modelType) && isAuthUser(),
      },
    }),
    [modelType, isAuthUser()],
  );
  const renderConfig = useMemo(() => config[mode], [mode, modelType]);

  useEffect(() => {
    if (modelType === "anime" && circular) {
      setCircular(false);
    }
  }, [modelType]);

  const generateCount = useMemo(() => {
    return isAuthUser() && isVip() ? statusStore.generateCount : 1;
  }, [isVip(), isAuthUser(), statusStore.generateCount]);

  const btnText = useMemo(() => {
    if (!generateCount || generateCount === 1) {
      const text = translate(Editor_Words["img2img-sd-submit"], lang);
      return text;
    } else {
      return translate(Editor_Words["img2img-sd-submit-and-add-template"], lang).replace(
        "$1",
        generateCount.toString(),
      );
    }
  }, [generateCount]);

  async function handleTranslate(): Promise<void> {
    const getModeData = (): {
      text: string;
      originalNegative: string;
      extraPromptInfo?: {
        promptSettings?: string[];
        negativePromptSettings?: string[];
      };
      style?: Txt2ImgStyles;
      artist?: Txt2ImgArtists;
      guidance_scale?: number;
      num_steps?: number;
      sampler?: SDSampler;
      seed?: number;
    } => {
      if (mode === "advance" && !isThirdParty(modelType)) {
        const promptKeyValueMap =
          wordTableStore[
            (MODEL_WORD_TABLE_SOURCES[modelType] ?? MODEL_WORD_TABLE_SOURCES.nolibox).name
          ].keyValueMap;
        const negativePromptKeyValueMap = wordTableStore.negativePromptKeywords.keyValueMap;
        return {
          text: statusStore.advancePrompt,
          originalNegative: statusStore.advanceModeNegativePrompt,
          extraPromptInfo: {
            promptSettings: statusStore.promptSettings?.filter((record) =>
              wordRecordSubmitFilter(statusStore.advancePrompt, record, promptKeyValueMap),
            ),
            negativePromptSettings: statusStore.negativePromptSettings?.filter((record) =>
              wordRecordSubmitFilter(
                statusStore.advanceModeNegativePrompt,
                record,
                negativePromptKeyValueMap,
              ),
            ),
          },
          guidance_scale: statusStore.guidance_scale,
          num_steps: statusStore.num_steps,
          sampler: statusStore.sampler,
          seed: statusStore.seed,
        };
      }
      const { txt2imgStyle: style, txt2imgArtist: artist, txt2imgTexts: text } = statusStore;
      const originalNegative = statusStore.optimizers?.length
        ? statusStore.optimizers.map((optimizeType) => optimizersMap[optimizeType].value).join(", ")
        : "";
      return {
        text,
        style,
        artist,
        originalNegative,
      };
    };
    const modeData = getModeData();
    const { text } = modeData;

    // sanity check
    if (!text) {
      toast(t, "warning", translate(Editor_Words["no-text-input-message"], lang));
      return;
    }
    if (!alias || (displayNode?.type !== "image" && displayNode?.type !== "path")) {
      return;
    }

    if (!isNoliboxModel(modelType)) return;

    // call api
    const _generateMetaFn = mode === "advance" ? getAdvanceModeMetaData : getTxt2ImgMetaData;

    const customEmbeddings =
      modelType === "anime"
        ? statusStore.animeSmallModels.concat()
        : statusStore.smallModels.concat();
    customEmbeddings.push(DEFAULT_NEGATIVE_PROMPT_EMBEDDING);

    const metaData = getImg2ImgMetaData({
      lang,
      text: modeData.text,
      circular,
      modelType,
      displayNode,
      originalNegative: modeData.originalNegative,
      extraPromptInfo: modeData.extraPromptInfo,
      txt2ImgCustomSize: {
        w: suitableWH.w,
        h: suitableWH.h,
      },
      customEmbeddings,
      url: "",
      fidelity,
      baseMetaFn: _generateMetaFn,
    });

    const extraAdvanceMetaData = {
      guidance_scale: modeData.guidance_scale,
      num_steps: modeData.num_steps,
      sampler: modeData.sampler,
      seed: modeData.seed,
    };
    if (extraAdvanceMetaData) {
      Object.assign(metaData, extraAdvanceMetaData);
    }

    console.log("metaData: ", metaData);

    // const metaData = {
    //   w: suitableWH.w,
    //   h: suitableWH.h,
    //   url: "",
    //   text: "",
    //   fidelity,
    //   use_circular: circular,
    //   is_anime: isAnime,
    //   seed: modeData.seed ?? getRandomSeed(),
    //   model: isNoliboxModel(modelType) ? MODEL_TYPES_INFO[modelType].metaModel : undefined,
    //   _guidance_scale: modeData.guidance_scale,
    //   get guidance_scale() {
    //     return this._guidance_scale;
    //   },
    //   set guidance_scale(value) {
    //     this._guidance_scale = value;
    //   },
    //   num_steps: modeData.num_steps,
    //   sampler: modeData.sampler,
    //   promptInfo: {
    //     lang,
    //     original: modeData.text,
    //     style: modeData.style,
    //     artist: modeData.artist,
    //     originalNegative: modeData.originalNegative,
    //     ...(modeData.extraPromptInfo ?? {}),
    //   },
    //   custom_embeddings:
    //     modelType === "anime" ? statusStore.animeSmallModels : statusStore.smallModels,
    // };

    await Actions.importMeta({
      t,
      lang,
      type: "img2img",
      meta: {
        type: "img2img",
        data: metaData,
      },
      setupInfo: { checkBusy: false, busyAliases: [alias] },
      aliasPrefix: "img2img",
      opt: {
        freezePrompt: false,
        displayNode,
        generateCount,
      },
    });
  }

  const renderLeftBody = (modelType: ModelTypes) => {
    if (modelType === "monkey") {
      return <MonkeyPanel src={url} />;
    } else if (modelType === "draft") {
      return <DraftPanel src={url} />;
    } else if (modelType === "wenxin") {
      return (
        <Box w="100%" pos="relative">
          <WenxinStatus />
          <LabelText display="flex" mt="16px" justifyContent="space-between">
            <Text>{translate(Editor_Words["prompt-for-img"], lang)}：</Text>
            <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
              <Text
                border="1px solid"
                pos="relative"
                px="10px"
                bg="#323232"
                borderRadius="4px"
                color="#b3b3b3"
                cursor="pointer"
                zIndex={1}
                _hover={{ color: "white" }}
              >
                {translate(Editor_Words["prompt-expand-modal-title"], lang)}
              </Text>
              <Text
                pos="absolute"
                px="6px"
                right={0}
                bottom="100%"
                bg="#36C19E"
                color="white"
                borderRadius="3px"
                fontSize="12px"
                _after={{
                  content: "''",
                  bg: "#36c19e",
                  w: "10px",
                  h: "10px",
                  pos: "absolute",
                  right: 0,
                  top: "80%",
                }}
              >
                new
              </Text>
            </Text>
          </LabelText>
          <Box h="fit-content" pos="relative" my="16px">
            <TextArea
              w="100%"
              m="0"
              lang={lang}
              textAreaH={300}
              minRows={5}
              maxRows={10}
              placeholderWord={Editor_Words["textarea-placeholder"]}
              text={statusStore.txt2imgTexts}
              setText={setTxt2ImgTexts}
            />
            {promptExpanding && (
              <Flex
                w="100%"
                h="100%"
                pos="absolute"
                top="0"
                left="0"
                flexGrow={1}
                bg="#323232"
                borderRadius="6px"
                border="1px solid white"
                justify="center"
                flexDir="column"
                align="center"
              >
                <Icon w="32px" h="32px" svg={robot} />
                <Text color="#b3b3b3">提示词生成中</Text>
              </Flex>
            )}
          </Box>
          <TextTrigger
            mt="-6px"
            panelTitle={translate(Editor_Words["txt2img-sd-style-title"], lang)}
            storeValue={wenxinStyle}
            setStoreValue={setWenxinStyle}
            popupGenerator={(value, setValue) => (
              <CapsulesPanel
                w="200px"
                p="12px"
                h={250}
                bg={panelBg}
                words={allWenxinStyles}
                callbackFn={(style: any) => async () => setValue(style)}
                focusingWord={value}
              />
            )}
            safeDistance={250 - 60}
            smartAlign={{
              offset: [24, 0],
              points: ["bl", "br"],
            }}
          />

          <InfoText>
            {translate(Editor_Words["model"], lang)}：
            {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
          </InfoText>
          <InfoText mt="12px">
            {translate(Wenxin_Words["wenxin-resolution"], lang)}
            {wenxinSubmitSize.split("*").join(" x ")} px
          </InfoText>
          <WenxinSubmitButton
            mt="48px"
            t={t}
            lang={lang}
            opt={{
              text,
              size: wenxinSubmitSize,
              style: wenxinStyle,
              url,
            }}
          />
        </Box>
      );
    }
    return (
      <Box w="100%">
        <ModeSwitch value={mode} onChange={setMode} mb="16px" />
        {renderConfig.content && (
          <LabelText display="flex" justifyContent="space-between">
            <Text>{translate(Editor_Words["img2img-prompt-title"], lang)}：</Text>
            <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
              <Text
                border="1px solid"
                pos="relative"
                px="10px"
                bg="#323232"
                borderRadius="4px"
                color="#b3b3b3"
                cursor="pointer"
                zIndex={1}
                _hover={{ color: "white" }}
              >
                {translate(Editor_Words["prompt-expand-modal-title"], lang)}
              </Text>
              <Text
                pos="absolute"
                px="6px"
                right={0}
                bottom="100%"
                bg="#36C19E"
                color="white"
                borderRadius="3px"
                fontSize="12px"
                _after={{
                  content: "''",
                  bg: "#36c19e",
                  w: "10px",
                  h: "10px",
                  pos: "absolute",
                  right: 0,
                  top: "80%",
                }}
              >
                new
              </Text>
            </Text>
          </LabelText>
        )}
        {renderConfig.prompt && (
          <LabelText display="flex" justifyContent="space-between">
            <Text>{translate(Editor_Words["prompt-input-title"], lang)}：</Text>
            <Text pos="relative" onClick={() => events.emit(MessageType.PROMPT_EXPAND)}>
              <Text
                border="1px solid"
                pos="relative"
                px="10px"
                bg="#323232"
                borderRadius="4px"
                color="#b3b3b3"
                cursor="pointer"
                zIndex={1}
                _hover={{ color: "white" }}
              >
                {translate(Editor_Words["prompt-expand-modal-title"], lang)}
              </Text>
              <Text
                pos="absolute"
                px="6px"
                right={0}
                bottom="100%"
                bg="#36C19E"
                color="white"
                borderRadius="3px"
                fontSize="12px"
                _after={{
                  content: "''",
                  bg: "#36c19e",
                  w: "10px",
                  h: "10px",
                  pos: "absolute",
                  right: 0,
                  top: "80%",
                }}
              >
                new
              </Text>
            </Text>
          </LabelText>
        )}
        {renderConfig.content && (
          <Box h="fit-content" pos="relative" my="16px">
            <TextArea
              w="100%"
              m="0"
              lang={lang}
              textAreaH={120}
              placeholderWord={Editor_Words["img2img-prompt-placeholder"]}
              text={text}
              setText={setTxt2ImgTexts}
            />
            {promptExpanding && (
              <Flex
                w="100%"
                h="100%"
                pos="absolute"
                top="0"
                left="0"
                flexGrow={1}
                bg="#323232"
                borderRadius="6px"
                border="1px solid white"
                justify="center"
                flexDir="column"
                align="center"
              >
                <Icon w="32px" h="32px" svg={robot} />
                <Text color="#b3b3b3">提示词生成中</Text>
              </Flex>
            )}
          </Box>
        )}
        {renderConfig.prompt && (
          <Box h="fit-content" pos="relative" my="16px">
            <TextArea
              w="100%"
              m="0"
              lang={lang}
              textAreaH={120}
              placeholderWord={Editor_Words["img2img-prompt-placeholder"]}
              text={advancePrompt}
              setText={setAdvancePrompt}
            />
            {promptExpanding && (
              <Flex
                w="100%"
                h="100%"
                pos="absolute"
                top="0"
                left="0"
                flexDir="column"
                flexGrow={1}
                bg="#323232"
                borderRadius="6px"
                border="1px solid white"
                justify="center"
                align="center"
              >
                <Icon w="32px" h="32px" svg={robot} />
                <Text color="#b3b3b3">提示词生成中</Text>
              </Flex>
            )}
          </Box>
        )}
        {isNoliboxModel(modelType) && renderConfig.promptWordTable && (
          <WordTable
            text={statusStore.advancePrompt}
            setText={setAdvancePrompt}
            title={translate(Editor_Words["prompt-word-table-title"], lang)}
            onSelect={(record) => {
              updatePromptSettings(record);
            }}
            sourceUrl={MODEL_WORD_TABLE_SOURCES[modelType].url}
            searchPlaceholder={Editor_Words["prompt-keyword-search-placeholder"]}
            defaultCategory="people"
            mt="16px"
            tableName={MODEL_WORD_TABLE_SOURCES[modelType].name}
          />
        )}
        {renderConfig.useNegativePrompt && (
          <NegativePromptSwitch
            value={statusStore.useNegativePrompt}
            onChange={setUseNegativePrompt}
          />
        )}
        {renderConfig.advanceModeNegativePrompt && statusStore.useNegativePrompt && (
          <TextArea
            w="100%"
            mb="16px"
            lang={lang}
            textAreaH={300}
            minRows={5}
            maxRows={10}
            placeholderWord={Editor_Words["negative-prompt-placeholder"]}
            text={statusStore.advanceModeNegativePrompt}
            setText={setAdvanceModeNegativePrompt}
          />
        )}
        {renderConfig.negativePromptWordTable && statusStore.useNegativePrompt && (
          <NegativePromptWordTable />
        )}
        {renderConfig.fidelity && <Fidelity value={fidelity} setValue={setFidelity} mt="16px" />}
        {renderConfig.style && (
          <StyleSelector value={statusStore.txt2imgStyle} setValue={setTxt2ImgStyle} />
        )}
        {renderConfig.artist && (
          <ArtistSelector value={statusStore.txt2imgArtist} setValue={setTxt2ImgArtist} />
        )}
        {renderConfig.smallModels && (
          <SmallModelsSelector
            type={NoliboxModelTypeEnum[modelType]}
            value={statusStore[NoliboxModelStatusNameEnum[modelType]]}
            onChange={updateSmallModelsMethod[modelType]}
          />
        )}
        {renderConfig.optimizer && (
          <OptimizerSelector mt="16px" value={statusStore.optimizers} setValue={setOptimizers} />
        )}

        {renderConfig.guidance_scale && (
          <NSlider
            mt="16px"
            value={statusStore.guidance_scale}
            max={30}
            min={-20}
            step={0.5}
            label={translate(Editor_Words["guidance-scale-caption"], lang)}
            onChange={(guidance_scale) => {
              setGuidanceScale(guidance_scale);
            }}
          />
        )}
        {renderConfig.num_steps && (
          <NSlider
            mt="16px"
            value={statusStore.num_steps}
            max={100}
            min={5}
            step={1}
            label={translate(Editor_Words["num-steps-caption"], lang)}
            onChange={(num_steps) => {
              setNumSteps(num_steps);
            }}
          />
        )}
        {renderConfig.sampler && (
          <SamplerSelector
            mt="16px"
            value={statusStore.sampler}
            onChange={(sampler) => {
              setSampler(sampler);
            }}
          />
        )}
        {renderConfig.seed && (
          <InputField
            mt="16px"
            lang={lang}
            identifier="advance-mode-seed"
            word={Editor_Words["seed-caption"]}
            value={statusStore.seed ?? -1}
            setValue={(seed) => {
              setSeed(seed);
            }}
            lower={-1}
          />
        )}
        {renderConfig.generateCount && isAuthUser() && (
          <GenMultiInput
            mt="16px"
            value={statusStore.generateCount}
            onChange={setGenerateCount}
            min={1}
            max={100}
          />
        )}

        {enableImg2Img ? (
          <InfoText fontSize="14px" mt="16px">
            <LabelText>{translate(Editor_Words["size"], lang)}：</LabelText>
            {translate(Editor_Words["stay-in-step-with-selected-pic"], lang)}
          </InfoText>
        ) : null}
        {enableImg2Img && alias && renderConfig.circular ? (
          <CircularSwitch alias={alias} circular={circular} onChange={setCircular} />
        ) : null}

        {enableImg2Img ? (
          <Flex flexDirection="column" gap="12px" mt="16px">
            <InfoText>
              {translate(Editor_Words["model"], lang)}：
              {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
            </InfoText>
            <InfoText>
              {translate(Editor_Words["resolution"], lang)}： {sizeString}
            </InfoText>
          </Flex>
        ) : null}

        <BizGenerateButton
          sticky
          mt="48px"
          size={suitableWH}
          generateCount={generateCount}
          calcPriceProps={GeneratePriceRules[GenerateBizType.Img2Img]}
          checkBusyConfig={{
            busyAlias: alias,
            checkVip: true,
          }}
          buttonProps={{
            isDisabled: !enableImg2Img,
          }}
          onSubmit={handleTranslate}
        >
          {btnText}
        </BizGenerateButton>
      </Box>
    );
  };

  return (
    <SubWithTitle
      pb="24px"
      flex={1}
      minH="100%"
      title={translate(Editor_Words["img2img-sd-title"], lang)}
    >
      <LabelText mt="16px">{translate(Editor_Words["img2img-placeholder-title"], lang)}:</LabelText>
      <ImageWithPlaceHolder url={url} placeholderText={placeholder} />
      <ModelSwitch
        enableModelTypes={!isAuthUser() ? NOLIBOX_MODEL_TYPES : MODEL_TYPES_WITH_THIRD_TYPES}
        mb="16px"
        onChange={handleModelChange}
      />
      {renderLeftBody(modelType)}
    </SubWithTitle>
  );
};

export const Img2ImgPanel = observer(Img2Img);
